import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, FileText, Copy, CheckCircle2, AlertTriangle, CheckCheck, Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const KIND_LABEL = {
  rfq: "Quote Request",
  quote: "Quote Request",
  kyc: "KYC Filing",
  refund: "Refund Claim",
  indirect_commission: "Indirect Commission",
  concierge: "Founders Concierge",
  order: "Order",
  motion: "Board Motion",
  inquiry: "Contact Inquiry",
  rectification: "Rectification Mandate",
  custodian_kit: "Major Custodian Kit",
};

const KIND_COLOR = {
  rfq: "#C9A961", quote: "#C9A961",
  kyc: "#2FB999", refund: "#C0392B",
  indirect_commission: "#C0392B",
  concierge: "#B87333", order: "#C9A961",
  motion: "#A6821E", inquiry: "#8A8A82",
};

const MONO = 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace';

const STATUS_META = {
  open:             { label: "Open",             fg: "#2FE4C6", bg: "rgba(47,228,198,0.12)",  ring: "rgba(47,228,198,0.45)" },
  in_progress:      { label: "In Progress",      fg: "#C9A961", bg: "rgba(201,169,97,0.14)",  ring: "rgba(201,169,97,0.5)" },
  awaiting_partner: { label: "Awaiting Partner", fg: "#E0A458", bg: "rgba(224,164,88,0.14)",  ring: "rgba(224,164,88,0.5)" },
  closed:           { label: "Closed",           fg: "#8A8A82", bg: "rgba(138,138,130,0.14)", ring: "rgba(138,138,130,0.4)" },
};
const STATUS_ORDER = ["open", "in_progress", "awaiting_partner", "closed"];

function AgeingBadge({ days, testid }) {
  return (
    <span data-testid={testid}
          className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 font-grotesk text-[9px] uppercase tracking-[0.22em] whitespace-nowrap"
          style={{ color: "#FF6B70", background: "rgba(229,72,77,0.14)", boxShadow: "inset 0 0 0 1px rgba(229,72,77,0.55)" }}>
      <span className="w-1.5 h-1.5 rounded-full bg-[#E5484D] animate-pulse" style={{ boxShadow: "0 0 8px #E5484D" }} />
      Ageing{typeof days === "number" ? ` · ${days}d` : ""}
    </span>
  );
}

export function SRNStatusBadge({ status, testid }) {
  const m = STATUS_META[status] || STATUS_META.open;
  return (
    <span data-testid={testid}
          className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 font-grotesk text-[9px] uppercase tracking-[0.22em] whitespace-nowrap"
          style={{ color: m.fg, background: m.bg, boxShadow: `inset 0 0 0 1px ${m.ring}` }}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: m.fg, boxShadow: status === "closed" ? "none" : `0 0 8px ${m.fg}` }} />
      {m.label}
    </span>
  );
}

export function AdminSRNLedger() {
  const { api } = useAuth();
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [srnQ, setSrnQ] = useState("");
  const [kindQ, setKindQ] = useState("");
  const [emailQ, setEmailQ] = useState("");
  const [statusQ, setStatusQ] = useState("");
  const [staleOnly, setStaleOnly] = useState(false);
  const [staleCount, setStaleCount] = useState(0);
  const [staleDays, setStaleDays] = useState(7);
  const [selected, setSelected] = useState(null);
  const [openCount, setOpenCount] = useState(null);
  const [bulkConfirm, setBulkConfirm] = useState(false);
  const [bulkNote, setBulkNote] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);

  const partnerEmail = emailQ.trim().toLowerCase();
  useEffect(() => {
    if (!partnerEmail.includes("@")) { setOpenCount(null); return; }
    let alive = true;
    api.get(`/admin/srn-ledger/open-count?email=${encodeURIComponent(partnerEmail)}`)
      .then((r) => { if (alive) setOpenCount(r.data.open_count); })
      .catch(() => { if (alive) setOpenCount(null); });
    return () => { alive = false; };
  }, [api, partnerEmail, rows]);

  const bulkClose = async () => {
    setBulkBusy(true);
    try {
      const r = await api.post("/admin/srn-ledger/bulk-close", { email: partnerEmail, note: bulkNote.trim() || null, notify: true });
      toast.success(`Closed ${r.data.closed} open SRN${r.data.closed === 1 ? "" : "s"} for ${partnerEmail}${r.data.emailed ? " · partner notified" : ""}`);
      setBulkConfirm(false); setBulkNote("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Bulk close failed"); }
    finally { setBulkBusy(false); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (srnQ.trim()) qs.set("srn", srnQ.trim());
      if (kindQ.trim()) qs.set("kind", kindQ.trim());
      if (emailQ.trim()) qs.set("email", emailQ.trim());
      if (statusQ) qs.set("status", statusQ);
      if (staleOnly) qs.set("stale", "true");
      const r = await api.get(`/admin/srn-ledger${qs.toString() ? `?${qs}` : ""}`);
      setRows(r.data.rows || []);
      setCounts(r.data.status_counts || {});
      setStaleCount(r.data.stale_count || 0);
      setStaleDays(r.data.stale_days || 7);
    } catch { toast.error("Could not load the SRN ledger."); }
    finally { setLoading(false); }
  }, [api, srnQ, kindQ, emailQ, statusQ, staleOnly]);
  useEffect(() => { load(); }, [load]);

  const openCase = async (srn) => {
    try {
      const r = await api.get(`/admin/srn-ledger/${encodeURIComponent(srn)}`);
      setSelected(r.data);
    } catch { toast.error("Could not open case file."); }
  };

  const setStatus = async (srn, status, note) => {
    const r = await api.patch(`/admin/srn-ledger/${encodeURIComponent(srn)}/status`, { status, note: note || null });
    setSelected((prev) => prev ? { ...prev, srn: r.data.srn } : prev);
    setRows((prev) => prev.map((x) => x.srn === srn ? { ...x, status: r.data.srn.status } : x));
    load();
    return r.data;
  };

  const copySrn = (srn) => {
    navigator.clipboard.writeText(srn); toast.success("SRN copied");
  };

  return (
    <div data-testid="admin-srn-ledger">
      {/* Header */}
      <div className="rounded-sm border border-[#C9A961]/40 bg-gradient-to-br from-[#141008] via-[#0f0d0a] to-[#141008] p-6 mb-6">
        <div className="font-grotesk text-[10px] uppercase tracking-[0.3em] text-[#C9A961]">
          · Vinci Master Ledger · Case File Search ·
        </div>
        <h3 className="font-serif text-2xl md:text-3xl text-[#F5F5F0] mt-2">SRN Ledger</h3>
        <p className="font-body text-sm text-[#C9C9C2] mt-2 leading-relaxed max-w-3xl">
          Every user-originated interaction is stamped with a Vinci Service
          Request Number. Paste an SRN (or a prefix), a kind, or a partner
          email to pull the full case file.
        </p>
      </div>

      {/* Filters */}
      <div className="grid md:grid-cols-4 gap-3 mb-3">
        <label className="flex items-center gap-2 rounded-sm border border-white/10 px-3 py-2 bg-black/40 focus-within:border-[#C9A961]">
          <Search size={14} className="text-[#8A8A82]" />
          <input
            data-testid="srn-filter-srn"
            placeholder="VNC-SRN-2026-…"
            value={srnQ}
            onChange={(e) => setSrnQ(e.target.value.toUpperCase())}
            style={{ fontFamily: MONO }}
            className="w-full bg-transparent outline-none text-sm text-[#F5F5F0] placeholder:text-[#6f6f68] tracking-[0.08em]"
          />
        </label>
        <select
          data-testid="srn-filter-kind"
          value={kindQ}
          onChange={(e) => setKindQ(e.target.value)}
          className="rounded-sm border border-white/10 bg-black/40 px-3 py-2 text-sm text-[#F5F5F0] outline-none focus:border-[#C9A961]"
        >
          <option value="">All kinds</option>
          {Object.entries(KIND_LABEL).map(([k, l]) => (
            <option key={k} value={k}>{l}</option>
          ))}
        </select>
        <input
          data-testid="srn-filter-email"
          placeholder="partner@example.com"
          value={emailQ}
          onChange={(e) => setEmailQ(e.target.value)}
          className="rounded-sm border border-white/10 bg-black/40 px-3 py-2 text-sm text-[#F5F5F0] outline-none focus:border-[#C9A961]"
        />
        <select
          data-testid="srn-filter-status"
          value={statusQ}
          onChange={(e) => setStatusQ(e.target.value)}
          className="rounded-sm border border-white/10 bg-black/40 px-3 py-2 text-sm text-[#F5F5F0] outline-none focus:border-[#C9A961]"
        >
          <option value="">All statuses</option>
          {STATUS_ORDER.map((s) => (
            <option key={s} value={s}>{STATUS_META[s].label}</option>
          ))}
        </select>
      </div>

      {/* Bulk close — appears once a partner email is filtered */}
      {partnerEmail.includes("@") && openCount !== null && (
        <div data-testid="srn-bulk-close-bar"
             className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-sm border border-[#C9A961]/40 bg-[#C9A961]/5 px-4 py-3">
          <div className="text-sm text-[#F5F5F0]">
            <span className="text-[#C9A961] font-semibold">{partnerEmail}</span> has{" "}
            <span data-testid="srn-open-count" className="font-semibold">{openCount}</span> open SRN{openCount === 1 ? "" : "s"}.
          </div>
          <button data-testid="srn-bulk-close-btn" disabled={!openCount} onClick={() => setBulkConfirm(true)}
                  className="inline-flex items-center gap-2 rounded-sm bg-[#C9A961] px-4 py-2 text-sm font-semibold text-black hover:bg-[#D8B75B] transition-colors disabled:opacity-40">
            <CheckCheck size={16} /> Close All Open ({openCount})
          </button>
        </div>
      )}
      {bulkConfirm && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-testid="srn-bulk-close-confirm">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => !bulkBusy && setBulkConfirm(false)} />
          <div className="relative w-full max-w-lg rounded-xl border border-[#C9A961]/50 bg-[#141414] p-8 shadow-2xl">
            <div className="text-sm uppercase tracking-wider text-[#C9A961]">Bulk close · confirmation</div>
            <h4 className="font-serif text-2xl text-[#F5F5F0] mt-2">Close {openCount} open SRN{openCount === 1 ? "" : "s"}?</h4>
            <p className="text-sm text-[#888888] mt-3">Every OPEN service request for <span className="text-[#F5F5F5]">{partnerEmail}</span> will be marked closed in one action and the partner receives a single digest email. In-progress and awaiting-partner cases are not touched.</p>
            <textarea data-testid="srn-bulk-close-note" value={bulkNote} onChange={(e) => setBulkNote(e.target.value)} placeholder="Optional resolution note for the partner"
                      className="mt-4 w-full rounded-sm border border-[#333333] bg-black/40 px-3 py-2 text-sm text-[#F5F5F0] outline-none focus:border-[#C9A961]" rows={3} />
            <div className="mt-6 flex justify-end gap-3">
              <button data-testid="srn-bulk-close-cancel" disabled={bulkBusy} onClick={() => setBulkConfirm(false)} className="rounded-sm border border-[#333333] px-4 py-2 text-sm text-[#D4D4D4] hover:border-[#C9A961]">Cancel</button>
              <button data-testid="srn-bulk-close-execute" disabled={bulkBusy} onClick={bulkClose} className="inline-flex items-center gap-2 rounded-sm bg-[#C9A961] px-5 py-2 text-sm font-semibold text-black hover:bg-[#D8B75B] disabled:opacity-50">
                {bulkBusy ? <Loader2 size={16} className="animate-spin" /> : <CheckCheck size={16} />} {bulkBusy ? "Closing…" : `Yes, close ${openCount}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Ageing nudge */}
      {staleCount > 0 && !staleOnly && (
        <div data-testid="srn-ageing-banner"
             className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-sm border px-4 py-3"
             style={{ borderColor: "rgba(229,72,77,0.55)", background: "rgba(229,72,77,0.08)",
                      boxShadow: "0 0 24px rgba(229,72,77,0.12)" }}>
          <div className="flex items-center gap-3">
            <AlertTriangle size={16} className="text-[#E5484D]" style={{ filter: "drop-shadow(0 0 6px rgba(229,72,77,0.7))" }} />
            <div>
              <div className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#E5484D]">Ageing cases · desk action required</div>
              <div className="font-body text-sm text-[#F5F5F0] mt-0.5">
                <span style={{ fontFamily: MONO }} className="text-[#E5484D]" data-testid="srn-ageing-count">{staleCount}</span>
                {" "}open SRN{staleCount === 1 ? " has" : "s have"} gone quiet for {staleDays}+ days — no status change, no reply.
              </div>
            </div>
          </div>
          <button onClick={() => { setStaleOnly(true); setStatusQ(""); }}
                  data-testid="srn-ageing-review"
                  className="rounded-sm bg-[#E5484D] px-4 py-2 font-grotesk text-[10px] uppercase tracking-[0.24em] text-white hover:bg-[#f05d62] shadow-[0_0_18px_rgba(229,72,77,0.35)]">
            Review ageing cases
          </button>
        </div>
      )}

      {/* Status pulse strip */}
      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="srn-status-strip">
        {STATUS_ORDER.map((s) => (
          <button key={s} onClick={() => { setStatusQ(statusQ === s ? "" : s); setStaleOnly(false); }}
                  data-testid={`srn-status-chip-${s}`}
                  className={`flex items-center gap-2 rounded-full border px-3 py-1 transition-colors ${
                    statusQ === s && !staleOnly ? "border-[#C9A961]" : "border-white/10 hover:border-white/30"}`}>
            <SRNStatusBadge status={s} />
            <span style={{ fontFamily: MONO }} className="text-xs text-[#F5F5F0]">{counts[s] || 0}</span>
          </button>
        ))}
        <button onClick={() => setStaleOnly((v) => !v)}
                data-testid="srn-status-chip-ageing"
                className={`flex items-center gap-2 rounded-full border px-3 py-1 transition-colors ${
                  staleOnly ? "border-[#E5484D]" : "border-white/10 hover:border-[#E5484D]/60"}`}
                style={staleOnly ? { background: "rgba(229,72,77,0.1)" } : undefined}>
          <AgeingBadge days={staleDays} />
          <span style={{ fontFamily: MONO }} className="text-xs text-[#F5F5F0]">{staleCount}</span>
        </button>
      </div>

      {/* Results */}
      {loading ? (
        <p className="font-body text-[#8A8A82]">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="font-body text-[#8A8A82]" data-testid="srn-empty">
          No SRN matches your filter.
        </p>
      ) : (
        <div className="border border-white/10 rounded-sm overflow-hidden">
          <div className="grid grid-cols-[1.6fr_1fr_1.2fr_1fr_1fr] gap-4 px-4 py-2.5 border-b border-white/10 bg-white/[0.02] font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#8A8A82]">
            <div>SRN</div><div>Kind</div><div>Partner</div><div>Filed</div><div>Status</div>
          </div>
          {rows.map((r) => (
            <button
              key={r.srn}
              onClick={() => openCase(r.srn)}
              data-testid={`srn-row-${r.srn}`}
              className="w-full grid grid-cols-[1.6fr_1fr_1.2fr_1fr_1fr] gap-4 px-4 py-3 border-b border-white/5 hover:bg-white/[0.04] text-left transition-colors items-center"
            >
              <span style={{ fontFamily: MONO }} className="text-[#C9A961] tracking-[0.06em] text-sm">{r.srn}</span>
              <span className="font-grotesk text-[10px] uppercase tracking-[0.2em]"
                style={{ color: KIND_COLOR[r.kind] || "#C9C9C2" }}>
                {KIND_LABEL[r.kind] || r.kind}
              </span>
              <span className="font-body text-xs text-[#C9C9C2] truncate">
                {r.email || "—"}
                {r.vinci_code && <span className="text-[#8A8A82]"> · {r.vinci_code}</span>}
              </span>
              <span className="font-body text-xs text-[#8A8A82]">
                {new Date(r.created_at).toLocaleString()}
              </span>
              <span className="flex items-center gap-1.5 flex-wrap">
                <SRNStatusBadge status={r.status || "open"} testid={`srn-status-${r.srn}`} />
                {r.ageing && <AgeingBadge days={r.age_days} testid={`srn-ageing-${r.srn}`} />}
              </span>
            </button>
          ))}
        </div>
      )}

      {selected && (
        <CaseFileModal
          bundle={selected}
          onClose={() => setSelected(null)}
          onCopy={copySrn}
          onSetStatus={setStatus}
        />
      )}
    </div>
  );
}

function CaseFileModal({ bundle, onClose, onCopy, onSetStatus }) {
  const { srn, entity, user, messages } = bundle;
  const current = srn.status || "open";
  const [pick, setPick] = useState(current);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setPick(current); }, [current]);

  const apply = async (status) => {
    if (status === "closed" && !window.confirm(`Close ${srn.srn}? The partner will be emailed a Case Closed notice.`)) return;
    setBusy(true);
    try {
      const r = await onSetStatus(srn.srn, status, note);
      toast.success(status === "closed"
        ? (r.emailed ? "SRN closed · partner notified" : "SRN closed")
        : `Status → ${STATUS_META[status].label}`);
      setNote("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update status");
    } finally { setBusy(false); }
  };

  const history = [...(srn.status_history || [])].reverse();
  return (
    <div
      data-testid="srn-case-file"
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm grid place-items-center p-6"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-2xl bg-[#0e0e0e] border border-[#C9A961]/45 rounded-sm p-8 md:p-10 max-h-[90vh] overflow-y-auto"
      >
        <button
          onClick={onClose}
          data-testid="srn-case-close"
          className="absolute top-4 right-4 rounded-full border border-white/20 w-8 h-8 grid place-items-center text-[#C9C9C2] hover:border-[#C9A961]"
        >×</button>

        <div className="flex items-center gap-3">
          <span className="grid place-items-center w-9 h-9 rounded-full bg-[#C9A961] text-black">
            <FileText size={18} />
          </span>
          <div>
            <div className="font-grotesk text-[10px] uppercase tracking-[0.3em] text-[#C9A961]">
              · Case File · Vinci Master Ledger ·
            </div>
            <div className="font-serif text-xl md:text-2xl text-[#F5F5F0] mt-1">
              {KIND_LABEL[srn.kind] || srn.kind}
            </div>
          </div>
        </div>

        <div className="mt-6 flex items-center justify-between gap-3 rounded-sm border border-[#C9A961]/40 bg-black/40 px-5 py-4">
          <div style={{ fontFamily: MONO }} className="text-[#C9A961] text-lg md:text-xl tracking-[0.1em] select-all" data-testid="srn-case-value">
            {srn.srn}
          </div>
          <div className="flex items-center gap-2">
            <SRNStatusBadge status={current} testid="srn-case-status-badge" />
            {srn.ageing && <AgeingBadge days={srn.age_days} testid="srn-case-ageing-badge" />}
            <button
              onClick={() => onCopy(srn.srn)}
              className="inline-flex items-center gap-1.5 rounded-full border border-[#C9A961]/40 px-3 py-1 font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#C9A961] hover:bg-[#C9A961]/10"
            >
              <Copy size={11} /> Copy
            </button>
          </div>
        </div>

        {/* Case lifecycle controls */}
        <div className="mt-5 rounded-sm border border-white/10 bg-white/[0.02] p-4" data-testid="srn-case-lifecycle">
          <div className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#C9A961] mb-3">
            Case Lifecycle
          </div>
          <div className="grid sm:grid-cols-[1fr_auto] gap-3 items-start">
            <div className="space-y-2">
              <select
                data-testid="srn-case-status-select"
                value={pick}
                onChange={(e) => setPick(e.target.value)}
                disabled={busy}
                className="w-full rounded-sm border border-white/10 bg-black/40 px-3 py-2 text-sm text-[#F5F5F0] outline-none focus:border-[#C9A961]"
              >
                {STATUS_ORDER.map((s) => (
                  <option key={s} value={s}>{STATUS_META[s].label}</option>
                ))}
              </select>
              <input
                data-testid="srn-case-status-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Optional note — included in the Case Closed email"
                className="w-full rounded-sm border border-white/10 bg-black/40 px-3 py-2 text-sm text-[#F5F5F0] outline-none focus:border-[#C9A961] placeholder:text-[#6f6f68]"
              />
            </div>
            <div className="flex sm:flex-col gap-2">
              <button
                data-testid="srn-case-apply-status"
                disabled={busy || pick === current}
                onClick={() => apply(pick)}
                className="rounded-sm border border-[#C9A961]/50 px-4 py-2 font-grotesk text-[10px] uppercase tracking-[0.24em] text-[#C9A961] hover:bg-[#C9A961]/10 disabled:opacity-40 whitespace-nowrap"
              >
                Update Status
              </button>
              {current === "closed" ? (
                <button
                  data-testid="srn-case-reopen"
                  disabled={busy}
                  onClick={() => apply("open")}
                  className="rounded-sm border border-[#2FE4C6]/50 px-4 py-2 font-grotesk text-[10px] uppercase tracking-[0.24em] text-[#2FE4C6] hover:bg-[#2FE4C6]/10 disabled:opacity-40 whitespace-nowrap"
                >
                  Reopen SRN
                </button>
              ) : (
                <button
                  data-testid="srn-case-close-srn"
                  disabled={busy}
                  onClick={() => apply("closed")}
                  className="inline-flex items-center justify-center gap-1.5 rounded-sm bg-[#C9A961] px-4 py-2 font-grotesk text-[10px] uppercase tracking-[0.24em] text-black hover:bg-[#dcbd77] disabled:opacity-40 whitespace-nowrap shadow-[0_0_18px_rgba(201,169,97,0.25)]"
                >
                  <CheckCircle2 size={12} /> Close SRN
                </button>
              )}
            </div>
          </div>
          {history.length > 0 && (
            <ol className="mt-4 space-y-2 border-t border-white/10 pt-3" data-testid="srn-case-status-history">
              {history.map((h, i) => (
                <li key={i} className="flex items-start gap-3 text-xs">
                  <span style={{ fontFamily: MONO }} className="text-[#8A8A82] whitespace-nowrap">
                    {new Date(h.at).toLocaleString()}
                  </span>
                  <span className="text-[#C9C9C2]">
                    <span style={{ color: STATUS_META[h.from]?.fg }}>{STATUS_META[h.from]?.label || h.from}</span>
                    {" → "}
                    <span style={{ color: STATUS_META[h.to]?.fg }}>{STATUS_META[h.to]?.label || h.to}</span>
                    <span className="text-[#8A8A82]"> · {h.by}</span>
                    {h.note && <div className="text-[#8A8A82] italic mt-0.5">“{h.note}”</div>}
                  </span>
                </li>
              ))}
            </ol>
          )}
        </div>

        <div className="mt-6 grid sm:grid-cols-2 gap-x-8 gap-y-3 font-body text-sm">
          <Row k="Filed on" v={new Date(srn.created_at).toLocaleString()} />
          <Row k="Kind" v={KIND_LABEL[srn.kind] || srn.kind} />
          {srn.closed_at && <Row k="Closed on" v={`${new Date(srn.closed_at).toLocaleString()} · ${srn.closed_by || ""}`} />}
          {user?.name && <Row k="Partner" v={user.name} />}
          {srn.email && <Row k="Email" v={srn.email} />}
          {user?.business_name && <Row k="Business" v={user.business_name} />}
          {user?.vinci_code && <Row k="Vinci Code" v={user.vinci_code} />}
          {user?.role && <Row k="Role" v={user.role} />}
          {srn.entity_id && <Row k="Linked entity" v={srn.entity_id} />}
        </div>

        {Array.isArray(messages) && messages.length > 0 && (
          <div className="mt-6" data-testid="srn-case-inbound-thread">
            <div className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#C9A961] mb-3">
              Inbound Thread · {messages.length} message{messages.length === 1 ? "" : "s"}
            </div>
            <div className="space-y-3">
              {messages.map((m) => (
                <div key={m.id} className="rounded-sm border border-white/10 bg-white/[0.02] p-4"
                     data-testid={`srn-inbound-${m.id}`}>
                  <div className="flex items-center justify-between gap-3 mb-2">
                    <div className="font-body text-xs text-[#F5F5F0] truncate">
                      <span className="text-[#C9A961]">{m.from_name || m.from_email}</span>
                      <span className="text-[#8A8A82]"> · {m.from_email}</span>
                    </div>
                    <div className="font-body text-[10px] text-[#8A8A82] whitespace-nowrap">
                      {m.received_at ? new Date(m.received_at).toLocaleString() : ""}
                    </div>
                  </div>
                  <div className="font-body text-xs text-[#F5F5F0] mb-2 truncate">{m.subject}</div>
                  <pre className="text-xs text-[#C9C9C2] whitespace-pre-wrap break-words leading-relaxed">
{m.body_text}
                  </pre>
                </div>
              ))}
            </div>
          </div>
        )}

        {entity && (
          <div className="mt-6 rounded-sm border border-white/10 p-4 bg-white/[0.02]">
            <div className="font-grotesk text-[10px] uppercase tracking-[0.24em] text-[#8A8A82] mb-2">
              Linked record · {entity.kind}
            </div>
            <pre className="text-xs text-[#C9C9C2] whitespace-pre-wrap break-words">
{JSON.stringify(entity.record, null, 2)}
            </pre>
          </div>
        )}

        {srn.meta && Object.keys(srn.meta || {}).length > 0 && (
          <div className="mt-4 rounded-sm border border-white/10 p-4 bg-white/[0.02]">
            <div className="font-grotesk text-[10px] uppercase tracking-[0.24em] text-[#8A8A82] mb-2">
              Meta
            </div>
            <pre className="text-xs text-[#C9C9C2] whitespace-pre-wrap break-words">
{JSON.stringify(srn.meta, null, 2)}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div>
      <div className="font-grotesk text-[9px] uppercase tracking-[0.28em] text-[#8A8A82]">{k}</div>
      <div className="text-[#C9C9C2] mt-1 break-words">{v}</div>
    </div>
  );
}
